import React from 'react';

import useCurrentCompany from 'features/sidebar/hooks/useCurrentCompany';

export const DEFAULT_UI_CONFIG = {
  courierSidePanel: {
    order: false,
    cashBalance: false,
    mapAndTimeline: false,
    performance: false,
    basicInfo: {
      status: false,
      task: false,
      courierDetailsLink: false,
    },
    details: {
      allowShiftReservation: false,
      isDisabled: false,
      capabilities: false,
      contractValidFrom: false,
      contractValidTo: false,
      maxDailyHours: false,
      maxWeeklyHours: false,
      showCourierDetailsLink: false,
      team: false,
    },
  },
  courierDetails: {
    status: false,
    overview: false,
    performance: false,
    history: false,
    performanceDetails: {
      travelledDistance: false,
    },
  },

  allCouriers: {
    uploadCouriers: false,
  },

  mainNavigation: {
    dashboard: false,
    overview: false,
    allCouriers: false,
    onboarding: {
      sidebar: false,
      route: false,
    },
    courierGroups: false,
    liveDeliveries: false,
    geolocationHistory: false,
    performance: false,
    financials: false,
    schedule: false,
    settings: false,
  },
};

export type ConfigUI = typeof DEFAULT_UI_CONFIG;

type DotNotationKeys<T, Prefix extends string = ''> = {
  [K in keyof T]: T[K] extends object
    ? DotNotationKeys<T[K], `${Prefix}${K & string}.`>
    : `${Prefix}${K & string}`;
}[keyof T];

function mapBooleansToFalse<T extends Record<string, unknown>>(obj: T): T {
  const result = {} as T;

  for (const key in obj) {
    const value = obj[key];

    if (typeof value === 'boolean') {
      result[key] = false as T[typeof key];
    } else if (typeof value === 'object' && value !== null) {
      result[key] = mapBooleansToFalse(value as Record<string, unknown>) as T[typeof key];
    } else {
      result[key] = value;
    }
  }

  return result;
}

function setDeepValueToTrue<T extends Record<string, unknown>>(obj: T, path: string[]): T {
  if (path.length === 0) return obj;

  const [currentKey, ...restPath] = path;

  return {
    ...obj,
    [currentKey]:
      restPath.length === 0
        ? true
        : obj[currentKey] && typeof obj[currentKey] === 'object'
          ? setDeepValueToTrue(obj[currentKey] as Record<string, unknown>, restPath)
          : obj[currentKey],
  };
}

export const enableConfigKeys = (
  configTemplate: ConfigUI,
  keysToEnable: Array<DotNotationKeys<ConfigUI>>,
): ConfigUI => {
  let newConfig = mapBooleansToFalse(structuredClone(configTemplate));

  for (const dotKey of keysToEnable) {
    const pathParts = dotKey.split('.');
    newConfig = setDeepValueToTrue(newConfig, pathParts);
  }

  return newConfig;
};

function getAllBooleanKeys<T extends object>(obj: T, prefix = ''): DotNotationKeys<T>[] {
  const keys: string[] = [];

  for (const key in obj) {
    const value = obj[key];
    const fullKey = prefix ? `${prefix}.${key}` : key;

    if (typeof value === 'boolean') {
      keys.push(fullKey);
    } else if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      keys.push(...getAllBooleanKeys(value, fullKey));
    }
  }

  return keys as DotNotationKeys<T>[];
}
export const getConfigByPartnership = (isCurrentCompanyPartnershipLCSP: boolean) => {
  if (isCurrentCompanyPartnershipLCSP) {
    const flags: Array<DotNotationKeys<ConfigUI>> = [
      'allCouriers.uploadCouriers',
      'courierDetails.performance',
      'courierSidePanel.performance',
      'mainNavigation.allCouriers',
      'mainNavigation.onboarding.route',
      'mainNavigation.performance',
      'mainNavigation.overview',
      'mainNavigation.schedule',
    ];

    return enableConfigKeys(DEFAULT_UI_CONFIG, flags);
  }

  return enableConfigKeys(DEFAULT_UI_CONFIG, getAllBooleanKeys(DEFAULT_UI_CONFIG));
};

const useConfigUI = () => {
  const { currentCompany } = useCurrentCompany();
  const { isCurrentCompanyPartnershipAvailable, isCurrentCompanyPartnershipLCSP } =
    currentCompany.data ?? {};

  const configUI = React.useMemo(() => {
    if (!isCurrentCompanyPartnershipAvailable) {
      return DEFAULT_UI_CONFIG;
    }

    return getConfigByPartnership(!!isCurrentCompanyPartnershipLCSP);
  }, [isCurrentCompanyPartnershipAvailable, isCurrentCompanyPartnershipLCSP]);

  return {
    data: configUI,
  };
};

export default useConfigUI;
